import { canvasHeight, canvasWidth, DefaultBloomColor, DefaultEntityColor, initialZoom } from './data/constants';
import { Camera } from './camera';
import { StageDef } from './data/maps';
import { Marble } from './marble';
import { ParticleManager } from './particleManager';
import { GameObject } from './gameObject';
import { UIObject } from './UIObject';
import { VectorLike } from './types/VectorLike';
import { MapEntityState } from './types/MapEntity.type';

export type RenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  winners: Marble[];
  particleManager: ParticleManager;
  effects: GameObject[];
  winnerRank: number;
  winner: Marble | null;
  size: VectorLike;
  totalMarbleCount: number;
  winnerCount: number;
};

export class RouletteRenderer {
  private _canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  public sizeFactor = 1;

  private _images: { [key: string]: HTMLImageElement } = {};
  private _chzzkDefaultImages: HTMLImageElement[] = []; // chzzk 1~22 기본 이미지 풀
  private _chzzkTrophyImage: HTMLImageElement | null = null; // 트로피 이미지
  private _title: string = '치지직 룰렛'; // 사용자 입력 타이틀

  constructor() {
  }

  get width() {
    return this._canvas.width;
  }

  get height() {
    return this._canvas.height;
  }

  get canvas() {
    return this._canvas;
  }

  async init() {
    await this._load();

    this._canvas = document.createElement('canvas');
    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    this.ctx = this._canvas.getContext('2d', {
      alpha: false,
    }) as CanvasRenderingContext2D;

    document.body.appendChild(this._canvas);

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries
        ? entries[0].contentRect
        : this._canvas.getBoundingClientRect();
      const width = Math.max(realSize.width / 2, 640);
      const height = (width / realSize.width) * realSize.height;
      this._canvas.width = width;
      this._canvas.height = height;
      this.sizeFactor = width / realSize.width;
    };

    const resizeObserver = new ResizeObserver(resizing);

    resizeObserver.observe(this._canvas);
    resizing();
  }

  private async _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((rs) => {
      const img = new Image();
      img.addEventListener('load', () => {
        rs(img);
      });
      img.src = url;
    });
  }

  private async _load(): Promise<void> {
    const loadPromises =
      [
        { name: '베이비부', imgUrl: new URL('../assets/images/chzzk_1.png', import.meta.url) },
        { name: '스마일부', imgUrl: new URL('../assets/images/chzzk_2.png', import.meta.url) },
        { name: '눈물부', imgUrl: new URL('../assets/images/chzzk_3.png', import.meta.url) },
        { name: '놀란부', imgUrl: new URL('../assets/images/chzzk_4.png', import.meta.url) },
        { name: '골프옐로부', imgUrl: new URL('../assets/images/chzzk_5.png', import.meta.url) },
        { name: '골프부', imgUrl: new URL('../assets/images/chzzk_6.png', import.meta.url) },
        { name: '테니스부', imgUrl: new URL('../assets/images/chzzk_7.png', import.meta.url) },
        { name: '행복부', imgUrl: new URL('../assets/images/chzzk_8.png', import.meta.url) },
        { name: '모자부', imgUrl: new URL('../assets/images/chzzk_9.png', import.meta.url) },
        { name: '선글라스부', imgUrl: new URL('../assets/images/chzzk_10.png', import.meta.url) },
        { name: '하트치즈', imgUrl: new URL('../assets/images/chzzk_11.png', import.meta.url) },
        { name: '통나무파워', imgUrl: new URL('../assets/images/chzzk_12.png', import.meta.url) },
        { name: '번개야구공', imgUrl: new URL('../assets/images/chzzk_13.png', import.meta.url) },
        { name: '풍선울리', imgUrl: new URL('../assets/images/chzzk_14.png', import.meta.url) },
        { name: '수영울리', imgUrl: new URL('../assets/images/chzzk_15.png', import.meta.url) },
        { name: '멍울리', imgUrl: new URL('../assets/images/chzzk_16.png', import.meta.url) },
        { name: '힙울리', imgUrl: new URL('../assets/images/chzzk_17.png', import.meta.url) },
        { name: '멍불리', imgUrl: new URL('../assets/images/chzzk_18.png', import.meta.url) },
        { name: '찡긋토비', imgUrl: new URL('../assets/images/chzzk_19.png', import.meta.url) },
        { name: '베이비토비', imgUrl: new URL('../assets/images/chzzk_20.png', import.meta.url) },
        { name: '수영토비', imgUrl: new URL('../assets/images/chzzk_21.png', import.meta.url) },
        { name: '치지직', imgUrl: new URL('../assets/images/chzzk_22.png', import.meta.url) },
      ].map(({ name, imgUrl }) => {
        return (async () => {
          this._images[name] = await this._loadImage(imgUrl.toString());
        })();
      });

    loadPromises.push((async () => {
      await this._loadImage(new URL('../assets/images/ff.svg', import.meta.url).toString());
    })());

    // chzzk 트로피 이미지 로드
    loadPromises.push(
      (async () => {
        try {
          this._chzzkTrophyImage = await this._loadImage(new URL('../assets/images/chzzk_trophy.png', import.meta.url).toString());
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );

    // chzzk 1~22 기본 이미지 로드 (명시되지 않은 이름들을 위해)
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_1.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_2.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_3.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_4.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_5.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_6.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_7.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_8.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_9.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_10.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_11.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_12.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_13.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_14.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_15.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_16.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_17.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_18.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_19.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_20.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_21.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );
    loadPromises.push(
      (async () => {
        try {
          const img = await this._loadImage(new URL('../assets/images/chzzk_22.png', import.meta.url).toString());
          this._chzzkDefaultImages.push(img);
        } catch {
          // 이미지 로드 실패 시 무시
        }
      })()
    );

    await Promise.all(loadPromises);
  }

  render(renderParameters: RenderParameters, uiObjects: UIObject[]) {
    this.ctx.fillStyle = 'black';
    this.ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt sans-serif';
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);
    renderParameters.camera.renderScene(this.ctx, () => {
      this.renderTrophy(renderParameters);
      this.renderEntities(renderParameters.entities);
      this.renderEffects(renderParameters);
      this.renderMarbles(renderParameters);
    });
    this.ctx.restore();

    // 타이틀은 HTML 요소로 표시 (index.html에서 처리)

    uiObjects.forEach((obj) =>
      obj.render(
        this.ctx,
        renderParameters,
        this._canvas.width,
        this._canvas.height,
      ),
    );
    renderParameters.particleManager.render(this.ctx);
    this.renderWinner(renderParameters);
  }

  private renderEntities(entities: MapEntityState[]) {
    this.ctx.save();
    entities.forEach((entity) => {
      const transform = this.ctx.getTransform();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle = entity.shape.color ?? DefaultEntityColor[entity.shape.type];
      this.ctx.strokeStyle = entity.shape.color ?? DefaultEntityColor[entity.shape.type];
      // 네온 효과 - polyline(벽)에만 강한 glow 적용
      if (entity.shape.type === 'polyline') {
        this.ctx.shadowBlur = 25;
        this.ctx.shadowColor = entity.shape.bloomColor ?? '#00FFA3';
        this.ctx.lineWidth = 0.15;
      } else {
        this.ctx.shadowBlur = 15;
        this.ctx.shadowColor = entity.shape.bloomColor ?? entity.shape.color ?? DefaultBloomColor[entity.shape.type];
      }
      const shape = entity.shape;
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box':
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }

      this.ctx.setTransform(transform);
    });
    this.ctx.restore();
  }

  private renderEffects({ effects, camera }: RenderParameters) {
    effects.forEach((effect) =>
      effect.render(this.ctx, camera.zoom * initialZoom),
    );
  }

  private renderMarbles({
                          marbles,
                          camera,
                          winnerRank,
                          winners,
                          size,
                        }: RenderParameters) {
    const winnerIndex = winnerRank - winners.length;

    const viewPort = { x: camera.x, y: camera.y, w: size.x, h: size.y, zoom: camera.zoom * initialZoom };
    marbles.forEach((marble, i) => {
      // 명시적으로 정의된 이름은 해당 이미지 사용, 그 외는 chzzk 1~9 랜덤 사용
      const marbleImage = this._images[marble.name] || this._getChzzkImageForName(marble.name);
      
      marble.render(
        this.ctx,
        camera.zoom * initialZoom,
        i === winnerIndex,
        false,
        marbleImage,
        viewPort,
      );
    });
  }

  // 이름을 해시하여 chzzk 1~22 중 하나를 일관되게 반환
  private _getChzzkImageForName(name: string): HTMLImageElement | undefined {
    if (this._chzzkDefaultImages.length === 0) return undefined;
    
    // 이름을 해시하여 0~21 범위의 인덱스 생성
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % this._chzzkDefaultImages.length;
    return this._chzzkDefaultImages[index];
  }

  private renderWinner({ winner, winnerCount }: RenderParameters) {
    // 우승자 수가 1명일 때만 크게 표시
    if (!winner || winnerCount > 1) return;
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this.ctx.fillRect(
      this._canvas.width / 2,
      this._canvas.height - 168,
      this._canvas.width / 2,
      168,
    );
    this.ctx.fillStyle = 'white';
    this.ctx.font = 'bold 48px sans-serif';
    this.ctx.textAlign = 'right';
    this.ctx.fillText(
      'Winner',
      this._canvas.width - 10,
      this._canvas.height - 120,
    );
    this.ctx.font = 'bold 72px sans-serif';
    this.ctx.fillStyle = winner.color;
    this.ctx.fillText(
      winner.name,
      this._canvas.width - 10,
      this._canvas.height - 55,
    );
    this.ctx.restore();
  }

  // 타이틀 설정 (HTML 요소로 표시)
  public setTitle(title: string) {
    this._title = title || '치지직 룰렛';
    // HTML 요소 업데이트
    const titleElement = document.getElementById('game-title');
    if (titleElement) {
      titleElement.textContent = this._title;
    }
  }

  // 맵의 도착지점에 트로피 이미지 표시
  private renderTrophy(renderParameters: RenderParameters) {
    if (!this._chzzkTrophyImage) return;
    
    // Chzzk Cheese Road 맵의 goalY는 245
    const goalX = 14; // 골인 지점 중앙 x 좌표
    const goalY = renderParameters.stage.goalY + 7;

    this.ctx.save();
    const trophySize = 4; // 월드 좌표 기준 크기
    const trophyX = goalX - trophySize / 2;
    const trophyY = goalY - trophySize - 1; // 골인 지점 바로 위

    this.ctx.drawImage(
      this._chzzkTrophyImage,
      trophyX,
      trophyY,
      trophySize,
      trophySize
    );
    this.ctx.restore();
  }
}
